/**
 * NoteList Component
 *
 * @component
 * @description This component displays a list of notes in an accordion-style container.
 * It allows adding new notes and managing the visibility of existing notes.
 *
 * @param {NoteData[]} props.notes - An array of NoteData objects representing the notes to be displayed.
 * @returns {JSX.Element} - A JSX element representing the NoteList component.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { isNull } from 'lodash';
import useTabsMenu from 'components/TabsNavigation/hooks/useTabsMenu';
import { LayoutContext } from 'context';
import useNotesContext from 'context/NotesContext';
import { ApproachActionOption } from '../../pages/ValuationsAllocation/components';
import { Tab, TabsNavigation } from '../TabsNavigation';
import Note from './note/Note';
import { NoteData, NoteListProps } from './types';

const NoteList = (props: NoteListProps) => {
  const { pageType, pageTypeId, onAddNote, onDeleteNote } = useNotesContext();
  const { viewOnlyUser } = useContext(LayoutContext);
  const { notes, isDisabled = false, appBarSx } = props;
  const actionsAreDisabled = isDisabled || viewOnlyUser;
  const [visibleNotes, setVisibleNotes] = useState<NoteData[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const handleOnAddNote = useCallback(() => {
    onAddNote?.({ pageType, pageTypeId } as unknown as NoteData);
  }, [onAddNote, pageType, pageTypeId]);

  useEffect(() => {
    if (!isNull(notes)) {
      const tempVisibleNotes = notes.filter(note => !note.is_deleted);
      setVisibleNotes(tempVisibleNotes);
    }
  }, [notes]);

  const getNoteLabel = (note: NoteData, i: number) => note.title || `Note ${i + 1}`;

  useEffect(() => {
    // only allow a single temp note at any given time...

    const hasTempNote = !!visibleNotes.find(i => i.temp_id);
    const noteTabs: Tab[] = visibleNotes.map((note, i) => ({
      content: <Note key={note.id ?? note.temp_id} note={note} isDisabled={actionsAreDisabled} />,
      disabled: actionsAreDisabled,
      id: note.id ? `${note.id}` : `${note.temp_id}`,
      label: getNoteLabel(note, i),
      menuItems: [
        ...(onDeleteNote
          ? [
            <ApproachActionOption<NoteData, never>
              key={`delete-${note.id ?? note.temp_id}`}
              actionText="Delete"
              argumentsList={[note]}
              tooltipMessage="Delete Note from the system"
              onClick={() => {
                onDeleteNote?.({ ...note, is_deleted: true });
                handleMenuClose();
              }}
            />,
          ]
          : []),
      ],
    }));

    if (onAddNote) {
      noteTabs.push({
        id: 'add-button',
        content: <></>,
        disabled: false,
        isActionTab: true,
        label: (
          <Tooltip title={hasTempNote ? 'Only one pending note allowed' : ''}>
            <span>
              <Button onClick={handleOnAddNote} disabled={hasTempNote || actionsAreDisabled}>
                +
              </Button>
            </span>
          </Tooltip>
        ),
      });
    }

    setTabs(noteTabs);

    // exhaustive dependencies are not needed here and result in degraded performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNotes, actionsAreDisabled]);

  return (
    <TabsNavigation
      menuAnchorEl={menuAnchorEl}
      mountOnActive
      handleMenuClose={handleMenuClose}
      handleMenuOpen={handleMenuOpen}
      relativeDrawer
      tabs={tabs}
      appBarSx={appBarSx}
    />
  );
};

export default NoteList;
